// @flow
import React, { Component } from 'react';
import Experience from 'app/common/react/components/experiences/Experience/Experience';
import Pulsar from 'app/common/react/ui/Pulsar';
import RichBoardIdeasTooltip from 'app/common/react/components/save/board/MoreIdeasTabTitle/RichBoardIdeasTooltip';
import { Box, Button, Flyout, Layer, Text, Touchable } from 'gestalt';

type Props = {
  anchor: ?HTMLElement,
  experienceIds?: Array<number>,
  flyoutSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number,
  fontSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  idealDirection?: 'up' | 'right' | 'down' | 'left',
  useMasonryFlyout?: boolean,
  //Enable customized complete button setting
  customizedComplete?: boolean,
  noClickToDismiss?: boolean,
  onClickComplete?: () => void,
  onClickDismiss?: () => void,
  placementId: number,
  shouldTimeoutDismiss?: boolean,
  textOverflow?: 'normal' | 'breakWord',
  textOverride?: string,
};

type DisplayDataProps = {
  has_pulsar?: boolean,
  has_tooltip?: boolean,
};

type State = {
  paused: boolean,
};

export default class ExperienceTooltip extends Component<Props, State> {
  static defaultProps = {
    fontSize: 'lg',
  };

  state = {
    paused: false,
  };

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  setDefaultPulsarTooltip(display_data: DisplayDataProps) {
    const isDefined = (val: ?boolean) => !(val === undefined || val === null);
    display_data.has_pulsar = isDefined(display_data.has_pulsar) ? display_data.has_pulsar : true;
    display_data.has_tooltip = isDefined(display_data.has_tooltip)
      ? display_data.has_tooltip
      : true;
  }

  handlePulsarClick = (hasTooltip: boolean, complete: () => void) => {
    if (hasTooltip) {
      this.setState({ paused: true });
    } else {
      complete();
    }
  };

  timer: TimeoutID;

  render() {
    const {
      anchor,
      textOverride,
      experienceIds,
      flyoutSize,
      fontSize,
      idealDirection,
      useMasonryFlyout,
      noClickToDismiss,
      onClickComplete,
      onClickDismiss,
      placementId,
      shouldTimeoutDismiss,
      textOverflow,
      customizedComplete,
    } = this.props;

    const FlyoutWrapper = ({ children }) =>
      useMasonryFlyout ? <Layer>{children}</Layer> : children;

    return (
      <Experience
        experienceIds={experienceIds}
        placementId={placementId}
        type={PCONST.thrift.ExperienceType.TOOLTIP}
      >
        {({ complete, dismiss, display_data }) => {
          this.setDefaultPulsarTooltip(display_data);
          if (!display_data.has_pulsar && !display_data.has_tooltip) {
            // this experience makes no sense, complete it
            complete();
            return null;
          }

          if (
            shouldTimeoutDismiss &&
            display_data.disappearTime &&
            display_data.disappearTime > 0
          ) {
            this.timer = setTimeout(() => {
              dismiss();
              return null;
            }, display_data.disappearTime);
          }

          return (
            <React.Fragment>
              {display_data.has_pulsar && (
                <Pulsar
                  anchor={anchor}
                  onTouch={() => this.handlePulsarClick(display_data.has_tooltip, complete)}
                  paused={this.state.paused}
                />
              )}
              {display_data.has_tooltip &&
                (!display_data.has_pulsar || this.state.paused) &&
                (display_data.thumbnail_urls ? (
                  <RichBoardIdeasTooltip
                    anchor={anchor}
                    dismiss={dismiss}
                    idealDirection={idealDirection || 'down'}
                    text={display_data.text}
                    thumbnails={display_data.thumbnail_urls}
                  />
                ) : (
                  <FlyoutWrapper>
                    <Flyout
                      anchor={anchor}
                      color="blue"
                      idealDirection={idealDirection || 'down'}
                      onDismiss={noClickToDismiss ? () => {} : dismiss}
                      positionRelativeToAnchor={!useMasonryFlyout}
                      shouldFocus={false}
                      size={flyoutSize}
                    >
                      <Box column={12} padding={3}>
                        <Text color="white" overflow={textOverflow} size={fontSize} weight="bold">
                          {!!textOverride
                            ? display_data.text.replace('{boardName}', textOverride)
                            : display_data.text}
                        </Text>
                        {(display_data.dismiss_button_text ||
                          display_data.complete_button_text) && (
                          <Box display="flex" alignItems={'center'} marginTop={2}>
                            {display_data.dismiss_button_text && (
                              <Box marginRight={1} column={6}>
                                <Button
                                  color="transparent"
                                  onClick={() => {
                                    if (onClickDismiss) {
                                      onClickDismiss();
                                    }
                                    dismiss();
                                  }}
                                  size="md"
                                  text={display_data.dismiss_button_text}
                                />
                              </Box>
                            )}
                            {display_data.complete_button_text && (
                              <Box column={display_data.dismiss_button_text ? 6 : 12}>
                                {customizedComplete ? (
                                  <Touchable
                                    onTouch={() => {
                                      complete();
                                      if (onClickComplete) {
                                        onClickComplete();
                                      }
                                    }}
                                    shape="rounded"
                                    fullHeight
                                  >
                                    <Box
                                      display="flex"
                                      color="white"
                                      padding={2}
                                      shape="rounded"
                                      justifyContent="center"
                                      dangerouslySetInlineStyle={{
                                        __style: { padding: '10px' },
                                      }}
                                    >
                                      <Text color="blue" weight="bold">
                                        {display_data.complete_button_text}
                                      </Text>
                                    </Box>
                                  </Touchable>
                                ) : (
                                  <Button
                                    color="white"
                                    size="md"
                                    text={display_data.complete_button_text}
                                    onClick={() => {
                                      complete();
                                      if (onClickComplete) {
                                        onClickComplete();
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Flyout>
                  </FlyoutWrapper>
                ))}
            </React.Fragment>
          );
        }}
      </Experience>
    );
  }
}
